import React, { useRef } from 'react';
import { Animated, Pressable, Text, View, StyleSheet } from 'react-native';
import { Paprika } from '../../../styles/theme';
import { DietDot } from '../../weekly/v2/primitives/DietDot';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import type { DietKind } from '../../../redux/mealPlanUISlice';
import { useTranslation } from 'react-i18next';

interface Props {
  slotKey: number;
  selected: boolean;
  portions: number;
  kind: DietKind;
  faint?: boolean;
  onPress: () => void;
  onLongPress?: () => void;
}

const KIND_LABEL: Record<DietKind, string> = {
  auto: 'mealConfig.auto',
  fish: 'mealConfig.fish',
  veg: 'mealConfig.veggie',
  meat: 'mealConfig.meat',
};

function kindToDietaryRole(kind: DietKind): DietaryRole | undefined {
  if (kind === 'fish') return 'fish';
  if (kind === 'veg') return 'vegetarian';
  return undefined;
}

export function ConfigCell({ selected, portions, kind, faint, onPress, onLongPress }: Props) {
  const { t } = useTranslation();
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scale, { toValue: 0.97, useNativeDriver: true, speed: 40 }).start();
  };
  const handlePressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 40 }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onLongPress={onLongPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <Animated.View
        style={[
          styles.cell,
          selected ? styles.selected : styles.unselected,
          faint && !selected && styles.faint,
          { transform: [{ scale }] },
        ]}
      >
        {selected ? (
          <>
            <View style={styles.checkCircle}>
              <Text style={styles.checkMark}>✓</Text>
            </View>
            <View style={styles.selectedInfo}>
              <Text style={styles.portionsLabel}>{portions} pers.</Text>
              {kind !== 'auto' && (
                <View style={styles.kindRow}>
                  <DietDot role={kindToDietaryRole(kind)} size={6} />
                  <Text style={styles.kindLabel}>{t(KIND_LABEL[kind])}</Text>
                </View>
              )}
            </View>
          </>
        ) : (
          <View style={styles.unselectedContent}>
            <Text style={[styles.plusIcon, faint && styles.faintText]}>+</Text>
            <Text style={[styles.suggestLabel, faint && styles.faintText]}>{t('thisWeek.suggestAction')}</Text>
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cell: {
    height: 118,
    borderRadius: 8,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  unselected: {
    borderStyle: 'dashed',
    borderColor: Paprika.border,
    backgroundColor: Paprika.bgSunk,
  },
  selected: {
    borderStyle: 'solid',
    borderColor: Paprika.brandSoft,
    backgroundColor: Paprika.brandTint,
  },
  faint: {
    opacity: 0.45,
  },
  checkCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: Paprika.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  checkMark: {
    color: '#fff',
    fontSize: 12,
    fontFamily: 'Inter-SemiBold',
  },
  selectedInfo: {
    alignItems: 'center',
    gap: 4,
  },
  portionsLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  kindRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  kindLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  unselectedContent: {
    alignItems: 'center',
    gap: 4,
  },
  plusIcon: {
    fontFamily: 'Inter-Regular',
    fontSize: 20,
    color: Paprika.textMuted,
    lineHeight: 22,
  },
  suggestLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  faintText: {
    color: Paprika.border,
  },
});
